
import React, {
  forwardRef,
  ReactNode,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';

import { Message } from './messages/models';
import {
  VisitorMessage,
  OperatorMessage,
  TypingMessage,
  ErrorMessage,
  SystemMessage,
  FileUploadMessage,
  FileUploadMessageProps,
} from './messages';
import CallingSpinner from './utils/CallingSpinner';
import ScrollButton from './utils/ScrollButton';
import LinkPreview, { LinkPreviewProps } from './messages/components/LinkPreview';

import { MessageTypes, MessageSubtypes } from './messages/constants';
import './style';

import createFileTransferExtraParams from '../chat-tools-area/utils/createFileTransferExtraParams';
import { ImgParams } from './messages/utils/AvatarImage';

type OperatorData = {
  nickName?: string;
  src: string;
  title: string;
}

type FileTransferOptions = {
  chash: string;
  connectionId: string;
  fileTransferSessionId: string;
  nickname: string;
}

interface MessageDisplayAreaOptions {
  showTime?: boolean;
  showSpinner?: boolean;
  showTypings?: boolean;
  showTranslatedIndicator?: boolean;
  fileTransfer?: FileTransferOptions;
  isChatShouldShowUp: boolean;
  initialScrollTop: number;
  scrollTop?: number;
  messageViewType?: string;
  operatorsData?: Record<string, OperatorData>;
  companyHeader?: ReactNode;
  customScrollConstructor?: any;
  nickname?: string;
  roomId?: string;
  customScrollTop?: number | null;
  unreadMessages: number;
  additionalMessageOptions?: Record<string, unknown>;
}

interface MessageDisplayAreaProps {
  messages: Message[];
  typingsMessages: Message[];
  texts: { [key: string]: string };
  options: MessageDisplayAreaOptions;
  dir?: string;
  onRemoveMessage?: (index: number) => void;
  onRepickFile?: (oldFilename: string, newFile: File) => void;
  onScroll?: (scrollTop: number) => void;
  onClick?: () => void;
  onNewMessage?: (text: string) => void;
  saveUnreadMessages?: (roomId: string, unreadMessages: number) => void;
}

export interface MessageRenderParams {
  imgParams: ImgParams;
  prevMessageParams?: Message;
  nextMessageParams?: Message;
  /* additionalMessageOptions */
  contextMenuForOperatorMessages?: (params: {
    children: ReactNode;
    id: string | number;
    originalText?: string;  
    nickname?: string;
    subtype?: string;
  }) => ReactNode;
  currentTalkerNickname?: string;
}

export interface CommonMessageProps {
  showTime?: boolean;
  showTranslatedIndicator?: boolean;
  texts: { [key: string]: string };
  messageViewType?: string;
  onScrollDown: () => void;
}

export interface MessageProps {
  message: Message
  renderParams: MessageRenderParams
  common: CommonMessageProps
}

export interface MessageDisplayAreaRef {
  onHide: () => void;
  onShow: () => void;
  scrollDown: () => void;
  setIsCalling: (isCalling: boolean) => void;
  getWrapper: () => HTMLDivElement | null;
  wrapperRef: HTMLDivElement | null;
  shouldScrollTopAfterOpen: boolean;
  isBlockInitialScroll: boolean;
}

const isFForLegacy = process.env.BROWSER === 'ff' || process.env.BROWSER === 'legacy';
const AUTOSCROLL_BORDER = 300;
const INITIAL_SCROLL_TIMEOUT = 500;

const MessageDisplayArea = forwardRef<MessageDisplayAreaRef, MessageDisplayAreaProps>(({ 
  messages,
  typingsMessages,
  texts,
  options,
  dir,
  onRemoveMessage,
  onRepickFile,
  onScroll,
  onClick,
  onNewMessage,
  saveUnreadMessages,
}, ref) => {
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const isCallingRef = useRef<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const customScrollRef = useRef<HTMLDivElement>(null);
  const customScrollVisitorRef = useRef<any | null>(null);
  const shouldScrollTopAfterOpenRef = useRef<boolean>(false);
  const isBlockInitialScrollRef = useRef<boolean>(false);
  const isWhenOpeningScrollToEndRef = useRef<boolean>(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const prevMessagesLengthRef = useRef(messages.length);
  const prevTypingsLengthRef = useRef(typingsMessages.length);
  const prevRoomIdRef = useRef(options.roomId);

  const instanceRef = useRef({
    isShow: options.isChatShouldShowUp,
    isWhenOpeningScrollToEnd: false,
    shouldScrollTopAfterOpen: false,
    reserveScrollTop: 0,
  });

  useEffect(() => {
    if (wrapperRef.current && customScrollRef.current) {
      scrollerRef.current = isFForLegacy ? customScrollRef.current : wrapperRef.current;
    }
  }, []);

  const scrollDown = useCallback(() => {
    if (!scrollerRef.current) return;

    if (!instanceRef.current.isShow && !scrollerRef.current.offsetHeight) {
      instanceRef.current.isWhenOpeningScrollToEnd = true;
    } else {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }

    if (!isAutoScrollEnabled) {
      setIsAutoScrollEnabled(true);
    }
    if (unreadMessages) {
      setUnreadMessages(0);
    }
    onScroll?.(scrollerRef.current.scrollTop);

  }, [onScroll, unreadMessages]);

  const checkShouldScroll = useCallback((lastMessage: Message) => {
    const { nickname } = options;

    if (!lastMessage || (lastMessage.subtype === MessageSubtypes.PREVIEW && !isAutoScrollEnabled)) return;

    if (lastMessage.subtype !== MessageSubtypes.PREVIEW && lastMessage.type !== 'typing') {
      onNewMessage?.(lastMessage.text)
    }

    if (isAutoScrollEnabled || lastMessage.nickname === nickname) {
      scrollDown();
      return;
    }
      
    if (lastMessage.type !== 'typing') {
      setUnreadMessages(prev => prev + 1);
    }
  }, [isAutoScrollEnabled, options.nickname, scrollDown, onNewMessage]);

  const setIsCalling = useCallback((isCalling: boolean): void => {
    isCallingRef.current = isCalling;
  }, []);

  const onHide = useCallback(() => {
    if (scrollerRef.current) {
      instanceRef.current.reserveScrollTop = scrollerRef.current.scrollTop;
    }
  }, []);

  const onShow = useCallback(() => {
    const { initialScrollTop } = options;

    instanceRef.current.isShow = true;

    if (shouldScrollTopAfterOpenRef.current) {
      if (initialScrollTop !== null) {
        scrollerRef.current!.scrollTop = initialScrollTop;
      }
    } else if (isWhenOpeningScrollToEndRef.current) {
      scrollDown();
    } else if (customScrollVisitorRef.current) {
      customScrollVisitorRef.current.changeScrollPositionByBarPosition?.();
    }

    shouldScrollTopAfterOpenRef.current = false;
    isWhenOpeningScrollToEndRef.current = false;
  }, []);

  const handleScroll = useCallback(() => {
    const scrollElement = isFForLegacy ? customScrollRef.current : wrapperRef.current;
    if (!scrollElement) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    const scrollBottom = scrollHeight - clientHeight - scrollTop;

    if (scrollBottom > AUTOSCROLL_BORDER && isAutoScrollEnabled) {
      setIsAutoScrollEnabled(false);
    } else if (scrollBottom <= AUTOSCROLL_BORDER && !isAutoScrollEnabled) {
      setIsAutoScrollEnabled(true);
      setUnreadMessages(0);
    }

    onScroll?.(scrollTop)
  }, [isAutoScrollEnabled, onScroll]);

  useEffect(() => {
    const { initialScrollTop, unreadMessages } = options;
    
    if (!instanceRef.current.isShow && scrollerRef.current?.offsetHeight) {
      instanceRef.current.isShow = true;
    }
    
    if (initialScrollTop !== null) {
      if (instanceRef.current.isShow && scrollerRef.current) {
        setTimeout(() => {
          if (!isBlockInitialScrollRef.current && scrollerRef.current) {
            scrollerRef.current.scrollTop = initialScrollTop;
            onScroll?.(initialScrollTop);
            handleScroll();
          }
        }, INITIAL_SCROLL_TIMEOUT);
      } else {
        shouldScrollTopAfterOpenRef.current = true;
      }
    } else {
      scrollDown();
    }
    
    setUnreadMessages(unreadMessages);

  const HostCustomScroll = options.customScrollConstructor;
    if (isFForLegacy && HostCustomScroll && customScrollRef.current) {
      customScrollVisitorRef.current = new HostCustomScroll(customScrollRef.current, initialScrollTop);
    }

    return () => {
      if (isFForLegacy && customScrollVisitorRef.current) {
        customScrollVisitorRef.current.unmount?.();
      }
    };
  }, []);

  useEffect(() => {
    instanceRef.current.isShow = options.isChatShouldShowUp;
  }, [options.isChatShouldShowUp]);

  useEffect(() => {
    const isNewMessage = messages.length > prevMessagesLengthRef.current;
    const isNewTyping = typingsMessages.length > prevTypingsLengthRef.current;
  
    if (isNewMessage || isNewTyping) {
      const lastMessage = isNewTyping ? typingsMessages[typingsMessages.length - 1] : messages[messages.length - 1];
      checkShouldScroll(lastMessage as Message);
    }
  
    const isRoomChanged = options.roomId !== prevRoomIdRef.current
    if (isRoomChanged) {
      if (options.customScrollTop !== null) {
        scrollerRef.current!.scrollTop = options.customScrollTop as number;
        handleScroll();
      } else {
        scrollDown();
      }
    
      if (saveUnreadMessages && prevRoomIdRef.current) {
        saveUnreadMessages(prevRoomIdRef.current, unreadMessages);
      }
      setUnreadMessages(options.unreadMessages);
    }
  
    prevMessagesLengthRef.current = messages.length;
    prevTypingsLengthRef.current = typingsMessages.length;
    prevRoomIdRef.current = options.roomId;
  }, [messages, typingsMessages, options.roomId, options.customScrollTop, options.unreadMessages, checkShouldScroll, handleScroll, scrollDown, saveUnreadMessages, unreadMessages]);

  const composeNotFullMessageRenderParams = useCallback((message: Message, index: number): MessageRenderParams => {
    const { operatorsData, additionalMessageOptions } = options
    return {
      ...additionalMessageOptions,
      imgParams: operatorsData && message.nickname ? operatorsData[message.nickname] : {},
      prevMessageParams: messages[index - 1],
      nextMessageParams: messages[index + 1] || -[0],
    };
  }, [options, messages, typingsMessages]);

  useImperativeHandle(ref, () => ({
    onHide,
    onShow,
    scrollDown,
    setIsCalling,
    getWrapper: () => wrapperRef.current,
    wrapperRef: wrapperRef.current,
    instanceRef: instanceRef.current,
    isAutoScrollEnabled,
    isCalling: isCallingRef.current,
    shouldScrollTopAfterOpen: shouldScrollTopAfterOpenRef.current,
    isBlockInitialScroll: isBlockInitialScrollRef.current,
  }), [onHide, onShow, scrollDown, setIsCalling, isAutoScrollEnabled]);

  const { showSpinner } = options;
  const wrapperClassName = `message-display-area custom-scroll ${options.messageViewType || ''}`;
  const common: CommonMessageProps = {
    showTime: options.showTime,
    showTranslatedIndicator: options.showTranslatedIndicator,
    texts,
    messageViewType: options.messageViewType,
    onScrollDown: scrollDown,
  }
  return (
      <>
        {!isAutoScrollEnabled && <ScrollButton unreadMessages={unreadMessages} onClick={scrollDown} />}
        
        <div
          className={wrapperClassName}
          dir={dir} ref={wrapperRef}
          onScroll={isFForLegacy ? undefined : handleScroll}
          onClick={onClick}
        >
          <div
            className="message-dispay-area__wrapper custom-scroll"
            ref={customScrollRef}
            onScroll={isFForLegacy ? handleScroll : undefined}
          >
            {options.companyHeader || null}
            {showSpinner && <CallingSpinner />}
            {messages.map((message, index) => {
              const messageProps: MessageProps = {
                message,
                renderParams: composeNotFullMessageRenderParams(message, index),
                common,
              }
              const isVisitorMessage = messageProps.message.type === MessageTypes.VISITOR;
                  
              switch (messageProps.message.type) {
                case MessageTypes.SYSTEM:
                  return <SystemMessage key={messageProps.message.id || index} {...messageProps} />;
                case MessageTypes.VISITOR:
                case MessageTypes.OPERATOR:
                  if (messageProps.message.subtype === MessageSubtypes.FILE_UPLOAD) {
                    if (!options.fileTransfer) {
                      return <ErrorMessage key={messageProps.message.id || index} {...messageProps} />
                    }
                    const submitExtraParams = createFileTransferExtraParams({
                      ...options.fileTransfer, 
                      clientTimestamp: messageProps.message.clientTimestamp 
                    });
                    return (
                      <FileUploadMessage
                        {...messageProps as FileUploadMessageProps}
                        key={messageProps.message.id || index}
                        onRemove={onRemoveMessage ? () => onRemoveMessage(index) : undefined}
                        onRepick={onRepickFile}
                        submitExtraParams={submitExtraParams}
                      />
                    );
                  }
                  if (messageProps.message.subtype === MessageSubtypes.PREVIEW) {
                    return (
                      <LinkPreview
                        key={messageProps.message.id || index}
                        {...messageProps as LinkPreviewProps}
                      />
                    );
                  }
                  
                  return isVisitorMessage 
                    ? <VisitorMessage key={messageProps.message.id || index} {...messageProps} />
                    : <OperatorMessage key={messageProps.message.id} {...messageProps} />;
                          
                default:
                  return <ErrorMessage key={messageProps.message.id || index} message={messageProps.message} />;
              }
            })}
  
            {options.showTypings && typingsMessages.map((messageParams: Message, index) => (
              <TypingMessage
                key={messageParams.id}
                message={messageParams}
                renderParams={{
                  ...composeNotFullMessageRenderParams(messageParams, index),
                  prevMessageParams: messages[messages.length - 1],
                }}
                common={common}
              />
            ))}
         </div>
       </div>
     </>
    );
});

export default MessageDisplayArea
